// src/services/institutionService.js
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  writeBatch,
  increment,
  limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

export const institutionService = {
  // ============ INSTITUTION BASIC METHODS ============
  
  // Get institution data (main book document)
  async getInstitutionData(bookId) {
    try {
      const bookDoc = await getDoc(doc(db, 'pb-books', bookId));
      if (!bookDoc.exists()) {
        throw new Error('Institution not found');
      }
      
      const bookData = { id: bookDoc.id, ...bookDoc.data() };
      
      // Also get sections data if they exist (similar to profiles)
      try {
        const sections = await this.getInstitutionSections(bookId);
        bookData.sections = sections;
      } catch (error) {
        console.warn('No sections found for institution:', error);
        bookData.sections = [];
      }
      
      return bookData;
    } catch (error) {
      console.error('Error getting institution data:', error);
      throw error;
    }
  },

  async getInstitutionSections(bookId) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const q = query(sectionsRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const sections = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return sections;
    } catch (error) {
      console.error('Error getting institution sections:', error);
      throw error;
    }
  },

  // NEW: Get complete institution data with all content
  async getCompleteInstitutionData(bookId) {
    try {
      console.log('Loading complete institution data for:', bookId);
      
      // Get main book data and sections
      const [mainData, programs, staff, events, posts, resources, facilities, galleries, admissions, stats] = await Promise.all([
        this.getInstitutionData(bookId),
        this.getAcademicPrograms(bookId).catch(() => []),
        this.getStaffMembers(bookId).catch(() => []),
        this.getEvents(bookId).catch(() => []),
        this.getPosts(bookId).catch(() => []),
        this.getResources(bookId).catch(() => []),
        this.getFacilities(bookId).catch(() => []),
        this.getGalleries(bookId).catch(() => []),
        this.getAdmissions(bookId).catch(() => []),
        this.getInstitutionStats(bookId).catch(() => ({}))
      ]);

      return {
        ...mainData,
        programs,
        staff,
        events,
        posts,
        resources,
        facilities,
        galleries,
        admissions,
        stats
      };
    } catch (error) {
      console.error('Error getting complete institution data:', error);
      throw error;
    }
  },

  // NEW: Add/Update institution section
  async addInstitutionSection(bookId, sectionData) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const docRef = await addDoc(sectionsRef, {
        ...sectionData,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...sectionData };
    } catch (error) {
      console.error('Error adding institution section:', error);
      throw error;
    }
  },

  async updateInstitutionSection(bookId, sectionId, sectionData) {
    try {
      const sectionRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      await updateDoc(sectionRef, {
        ...sectionData,
        updatedAt: serverTimestamp()
      });
      
      return sectionData;
    } catch (error) {
      console.error('Error updating institution section:', error);
      throw error;
    }
  },

  async deleteInstitutionSection(bookId, sectionId) {
    try {
      const sectionRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      await deleteDoc(sectionRef);
    } catch (error) {
      console.error('Error deleting institution section:', error);
      throw error;
    }
  },

  // Update institution main data
  async updateInstitution(bookId, institutionData) {
    try {
      const bookRef = doc(db, 'pb-books', bookId);
      await updateDoc(bookRef, {
        ...institutionData,
        updatedAt: serverTimestamp()
      });
      
      return institutionData;
    } catch (error) {
      console.error('Error updating institution:', error);
      throw error;
    }
  },

  // Get institution statistics
  async getInstitutionStats(bookId) {
    try {
      const [programs, staff, events, posts, resources, facilities, admissions] = await Promise.all([
        getDocs(collection(db, 'pb-books', bookId, 'pb-programs')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-staff')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-events')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-posts')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-resources')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-facilities')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-admissions'))
      ]);

      return {
        programs: programs.size,
        staff: staff.size,
        events: events.size,
        posts: posts.size,
        resources: resources.size,
        facilities: facilities.size,
        admissions: admissions.size,
        totalContent: programs.size + staff.size + events.size + posts.size + resources.size + facilities.size
      };
    } catch (error) {
      console.error('Error getting institution stats:', error);
      return {
        programs: 0,
        staff: 0,
        events: 0,
        posts: 0,
        resources: 0,
        facilities: 0,
        admissions: 0,
        totalContent: 0
      };
    }
  },

  // ============ ACADEMIC PROGRAMS ============
  
  async getAcademicPrograms(bookId) {
    try {
      const programsRef = collection(db, 'pb-books', bookId, 'pb-programs');
      const q = query(programsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting academic programs:', error);
      throw error;
    }
  },

  async createAcademicProgram(bookId, programData) {
    try {
      const programsRef = collection(db, 'pb-books', bookId, 'pb-programs');
      const docRef = await addDoc(programsRef, {
        ...programData,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...programData };
    } catch (error) {
      console.error('Error creating academic program:', error);
      throw error;
    }
  },

  async updateAcademicProgram(bookId, programId, programData) {
    try {
      const programRef = doc(db, 'pb-books', bookId, 'pb-programs', programId);
      await updateDoc(programRef, {
        ...programData,
        updatedAt: serverTimestamp()
      });
      
      return programData;
    } catch (error) {
      console.error('Error updating academic program:', error);
      throw error;
    }
  },

  async deleteAcademicProgram(bookId, programId) {
    try {
      const programRef = doc(db, 'pb-books', bookId, 'pb-programs', programId);
      await deleteDoc(programRef);
    } catch (error) {
      console.error('Error deleting academic program:', error);
      throw error;
    }
  },

  // ============ STAFF MANAGEMENT ============
  
  async getStaffMembers(bookId) {
    try {
      const staffRef = collection(db, 'pb-books', bookId, 'pb-staff');
      const q = query(staffRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting staff members:', error);
      throw error;
    }
  },

  async createStaffMember(bookId, staffData) {
    try {
      const staffRef = collection(db, 'pb-books', bookId, 'pb-staff');
      const docRef = await addDoc(staffRef, {
        ...staffData,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...staffData };
    } catch (error) {
      console.error('Error creating staff member:', error);
      throw error;
    }
  },

  async updateStaffMember(bookId, staffId, staffData) {
    try {
      const staffRef = doc(db, 'pb-books', bookId, 'pb-staff', staffId);
      await updateDoc(staffRef, {
        ...staffData,
        updatedAt: serverTimestamp()
      });
      
      return staffData;
    } catch (error) {
      console.error('Error updating staff member:', error);
      throw error;
    }
  },

  async deleteStaffMember(bookId, staffId) {
    try {
      const staffRef = doc(db, 'pb-books', bookId, 'pb-staff', staffId);
      await deleteDoc(staffRef);
    } catch (error) {
      console.error('Error deleting staff member:', error);
      throw error;
    }
  },

  // ============ EVENTS MANAGEMENT ============
  
  async getEvents(bookId) {
    try {
      const eventsRef = collection(db, 'pb-books', bookId, 'pb-events');
      const q = query(eventsRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting events:', error);
      throw error;
    }
  },

  async createEvent(bookId, eventData) {
    try {
      const eventsRef = collection(db, 'pb-books', bookId, 'pb-events');
      const docRef = await addDoc(eventsRef, {
        ...eventData,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...eventData };
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  },

  async updateEvent(bookId, eventId, eventData) {
    try {
      const eventRef = doc(db, 'pb-books', bookId, 'pb-events', eventId);
      await updateDoc(eventRef, {
        ...eventData,
        updatedAt: serverTimestamp()
      });
      
      return eventData;
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  },

  async deleteEvent(bookId, eventId) {
    try {
      const eventRef = doc(db, 'pb-books', bookId, 'pb-events', eventId);
      await deleteDoc(eventRef);
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  },

  // ============ NEWS MANAGEMENT ============
  
  async getPosts(bookId) {
    try {
      const postRef = collection(db, 'pb-books', bookId, 'pb-posts');
      const q = query(postRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting posts:', error);
      throw error;
    }
  },

  async createPost(bookId, postData) {
    try {
      const postRef = collection(db, 'pb-books', bookId, 'pb-posts');
      const docRef = await addDoc(postRef, {
        ...postData,
        bookId,
        views: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...postData };
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  },

  async updatePost(bookId, postId, postData) {
    try {
      const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
      await updateDoc(postRef, {
        ...postData,
        updatedAt: serverTimestamp()
      });
      
      return postData;
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  },

  async deletePost(bookId, postId) {
    try {
      const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
      await deleteDoc(postRef);
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  },

  // ============ RESOURCES MANAGEMENT ============
  
  async getResources(bookId) {
    try {
      const resourcesRef = collection(db, 'pb-books', bookId, 'pb-resources');
      const q = query(resourcesRef, orderBy('title', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting resources:', error);
      throw error;
    }
  },

  async createResource(bookId, resourceData) {
    try {
      const resourcesRef = collection(db, 'pb-books', bookId, 'pb-resources');
      
      // Enhanced resource data structure
      const enhancedData = {
        ...resourceData,
        bookId,
        downloads: 0,
        views: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Resource type-specific fields
        ...(resourceData.resourceType === 'physical' && {
          status: resourceData.status || 'available',
          assetId: resourceData.assetId || this.generateAssetId(),
          location: resourceData.location || '',
          purchaseDate: resourceData.purchaseDate || null,
          warranty: resourceData.warranty || null,
          value: resourceData.value || 0,
          condition: resourceData.condition || 'good'
        }),
        
        ...(resourceData.resourceType === 'learning' && {
          subject: resourceData.subject || '',
          gradeLevel: resourceData.gradeLevel || '',
          difficulty: resourceData.difficulty || 'intermediate',
          duration: resourceData.duration || null
        }),
        
        ...(resourceData.resourceType === 'management' && {
          department: resourceData.department || '',
          priority: resourceData.priority || 'medium',
          reviewDate: resourceData.reviewDate || null,
          approvedBy: resourceData.approvedBy || null
        })
      };
      
      const docRef = await addDoc(resourcesRef, enhancedData);
      return { id: docRef.id, ...enhancedData };
    } catch (error) {
      console.error('Error creating resource:', error);
      throw error;
    }
  },

  // Generate unique asset ID for physical resources
  generateAssetId() {
    const prefix = 'AST';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substr(2, 4).toUpperCase();
    return `${prefix}${timestamp}${random}`;
  },

  // Get resources with enhanced filtering
  async getResourcesByType(bookId, resourceType, category = null) {
    try {
      const resourcesRef = collection(db, 'pb-books', bookId, 'pb-resources');
      let q = query(
        resourcesRef, 
        where('resourceType', '==', resourceType),
        orderBy('updatedAt', 'desc')
      );
      
      if (category) {
        q = query(q, where('category', '==', category));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting resources by type:', error);
      throw error;
    }
  },

  // Update resource status (especially useful for physical resources)
  async updateResourceStatus(bookId, resourceId, status, additionalData = {}) {
    try {
      const resourceRef = doc(db, 'pb-books', bookId, 'pb-resources', resourceId);
      await updateDoc(resourceRef, {
        status,
        ...additionalData,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating resource status:', error);
      throw error;
    }
  },

  // Get resource statistics
  async getResourceStats(bookId) {
    try {
      const resourcesRef = collection(db, 'pb-books', bookId, 'pb-resources');
      const snapshot = await getDocs(resourcesRef);
      
      const stats = {
        total: 0,
        physical: 0,
        learning: 0,
        management: 0,
        byStatus: {},
        byCategory: {},
        totalValue: 0
      };
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        stats.total++;
        stats[data.resourceType] = (stats[data.resourceType] || 0) + 1;
        
        // Status stats for physical resources
        if (data.resourceType === 'physical' && data.status) {
          stats.byStatus[data.status] = (stats.byStatus[data.status] || 0) + 1;
          if (data.value) {
            stats.totalValue += data.value;
          }
        }
        
        // Category stats
        if (data.category) {
          stats.byCategory[data.category] = (stats.byCategory[data.category] || 0) + 1;
        }
      });
      
      return stats;
    } catch (error) {
      console.error('Error getting resource stats:', error);
      throw error;
    }
  },

  async updateResource(bookId, resourceId, resourceData) {
    try {
      const resourceRef = doc(db, 'pb-books', bookId, 'pb-resources', resourceId);
      await updateDoc(resourceRef, {
        ...resourceData,
        updatedAt: serverTimestamp()
      });
      
      return resourceData;
    } catch (error) {
      console.error('Error updating resource:', error);
      throw error;
    }
  },

  async deleteResource(bookId, resourceId) {
    try {
      const resourceRef = doc(db, 'pb-books', bookId, 'pb-resources', resourceId);
      await deleteDoc(resourceRef);
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  },

  // ============ FACILITIES MANAGEMENT ============
  
  async getFacilities(bookId) {
    try {
      const facilitiesRef = collection(db, 'pb-books', bookId, 'pb-facilities');
      const q = query(facilitiesRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting facilities:', error);
      throw error;
    }
  },

  async createFacility(bookId, facilityData) {
    try {
      const facilitiesRef = collection(db, 'pb-books', bookId, 'pb-facilities');
      const docRef = await addDoc(facilitiesRef, {
        ...facilityData,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...facilityData };
    } catch (error) {
      console.error('Error creating facility:', error);
      throw error;
    }
  },

  async updateFacility(bookId, facilityId, facilityData) {
    try {
      const facilityRef = doc(db, 'pb-books', bookId, 'pb-facilities', facilityId);
      await updateDoc(facilityRef, {
        ...facilityData,
        updatedAt: serverTimestamp()
      });
      
      return facilityData;
    } catch (error) {
      console.error('Error updating facility:', error);
      throw error;
    }
  },

  async deleteFacility(bookId, facilityId) {
    try {
      const facilityRef = doc(db, 'pb-books', bookId, 'pb-facilities', facilityId);
      await deleteDoc(facilityRef);
    } catch (error) {
      console.error('Error deleting facility:', error);
      throw error;
    }
  },

  // ============ ADMISSIONS MANAGEMENT ============
  
  async getAdmissions(bookId) {
    try {
      const admissionsRef = collection(db, 'pb-books', bookId, 'pb-admissions');
      const q = query(admissionsRef, orderBy('appliedDate', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting admissions:', error);
      throw error;
    }
  },

  async createAdmission(bookId, admissionData) {
    try {
      const admissionsRef = collection(db, 'pb-books', bookId, 'pb-admissions');
      const docRef = await addDoc(admissionsRef, {
        ...admissionData,
        bookId,
        appliedDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...admissionData };
    } catch (error) {
      console.error('Error creating admission:', error);
      throw error;
    }
  },

  async updateAdmission(bookId, admissionId, admissionData) {
    try {
      const admissionRef = doc(db, 'pb-books', bookId, 'pb-admissions', admissionId);
      await updateDoc(admissionRef, {
        ...admissionData,
        updatedAt: serverTimestamp()
      });
      
      return admissionData;
    } catch (error) {
      console.error('Error updating admission:', error);
      throw error;
    }
  },

  async deleteAdmission(bookId, admissionId) {
    try {
      const admissionRef = doc(db, 'pb-books', bookId, 'pb-admissions', admissionId);
      await deleteDoc(admissionRef);
    } catch (error) {
      console.error('Error deleting admission:', error);
      throw error;
    }
  },

  // ============ GALLERIES MANAGEMENT ============
  
  async getGalleries(bookId) {
    try {
      const galleriesRef = collection(db, 'pb-books', bookId, 'pb-galleries');
      const q = query(galleriesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting galleries:', error);
      throw error;
    }
  },

  async createGallery(bookId, galleryData) {
    try {
      const galleriesRef = collection(db, 'pb-books', bookId, 'pb-galleries');
      const docRef = await addDoc(galleriesRef, {
        ...galleryData,
        bookId,
        views: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...galleryData };
    } catch (error) {
      console.error('Error creating gallery:', error);
      throw error;
    }
  },

  async updateGallery(bookId, galleryId, galleryData) {
    try {
      const galleryRef = doc(db, 'pb-books', bookId, 'pb-galleries', galleryId);
      await updateDoc(galleryRef, {
        ...galleryData,
        updatedAt: serverTimestamp()
      });
      
      return galleryData;
    } catch (error) {
      console.error('Error updating gallery:', error);
      throw error;
    }
  },

  async deleteGallery(bookId, galleryId) {
    try {
      const galleryRef = doc(db, 'pb-books', bookId, 'pb-galleries', galleryId);
      await deleteDoc(galleryRef);
    } catch (error) {
      console.error('Error deleting gallery:', error);
      throw error;
    }
  },

  // ============ FILE UPLOAD METHODS ============
  
  // Upload image files
  async uploadImage(file, bookId, category = 'general') {
    try {
      const timestamp = Date.now();
      const fileName = `${category}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `pb-institutions/${bookId}/${category}/${fileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  // Upload document files
  async uploadDocument(file, bookId, category = 'documents') {
    try {
      const timestamp = Date.now();
      const fileName = `${category}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `pb-institutions/${bookId}/${category}/${fileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  // Delete file from storage
  async deleteFile(fileUrl) {
    try {
      const fileRef = ref(storage, fileUrl);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  },

  // ============ ANALYTICS METHODS ============
  
  async getAnalytics(bookId, options = {}) {
    try {
      const { timeRange = 30, includeDetailed = false } = options;
      
      // Get basic stats
      const stats = await this.getInstitutionStats(bookId);
      
      // Mock analytics data - in production, you'd integrate with actual analytics service
      const mockAnalytics = {
        totalViews: Math.floor(Math.random() * 1000) + 500,
        uniqueVisitors: Math.floor(Math.random() * 300) + 150,
        avgSessionDuration: '3:45',
        pageViews: Math.floor(Math.random() * 2000) + 800,
        viewsGrowth: Math.floor(Math.random() * 20) + 5,
        visitorsGrowth: Math.floor(Math.random() * 15) + 3,
        sessionGrowth: Math.floor(Math.random() * 10) + 2,
        pageViewsGrowth: Math.floor(Math.random() * 25) + 8,
        ...stats
      };

      if (includeDetailed) {
        mockAnalytics.popularSections = [
          { name: 'Academic Programs', views: Math.floor(Math.random() * 200) + 100 },
          { name: 'Staff Directory', views: Math.floor(Math.random() * 150) + 80 },
          { name: 'Posts & Events', views: Math.floor(Math.random() * 120) + 60 },
          { name: 'Facilities', views: Math.floor(Math.random() * 100) + 50 }
        ];

        mockAnalytics.trafficSources = [
          { name: 'Direct', percentage: 45 },
          { name: 'Google Search', percentage: 30 },
          { name: 'Social Media', percentage: 15 },
          { name: 'Referrals', percentage: 10 }
        ];

        mockAnalytics.recentActivity = [
          { action: 'New program added', details: 'Computer Science Program', timestamp: '2 hours ago' },
          { action: 'Staff profile updated', details: 'Dr. Jane Smith', timestamp: '4 hours ago' },
          { action: 'Event published', details: 'Annual Science Fair', timestamp: '1 day ago' }
        ];
      }

      return mockAnalytics;
    } catch (error) {
      console.error('Error getting analytics:', error);
      throw error;
    }
  },

  async exportAnalytics(bookId, options = {}) {
    try {
      const analytics = await this.getAnalytics(bookId, { ...options, includeDetailed: true });
      
      // Generate CSV data
      const csvData = [
        'Metric,Value',
        `Total Views,${analytics.totalViews}`,
        `Unique Visitors,${analytics.uniqueVisitors}`,
        `Page Views,${analytics.pageViews}`,
        `Programs,${analytics.programs}`,
        `Staff Members,${analytics.staff}`,
        `Events,${analytics.events}`,
        `Posts,${analytics.Posts}`,
        `Resources,${analytics.resources}`,
        `Facilities,${analytics.facilities}`
      ].join('\n');
      
      return csvData;
    } catch (error) {
      console.error('Error exporting analytics:', error);
      throw error;
    }
  },

  // ============ SEARCH AND UTILITY METHODS ============
  
  async searchContent(bookId, searchTerm, categories = []) {
    try {
      const searches = [];
      
      // Search in different collections based on categories or all if none specified
      const collectionsToSearch = categories.length > 0 ? categories : 
        ['pb-programs', 'pb-staff', 'pb-events', 'pb-posts', 'pb-resources', 'pb-facilities'];
      
      for (const collectionName of collectionsToSearch) {
        const collectionRef = collection(db, 'pb-books', bookId, collectionName);
        const snapshot = await getDocs(collectionRef);
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const searchableText = `${data.name || data.title || ''} ${data.description || ''}`.toLowerCase();
          
          if (searchableText.includes(searchTerm.toLowerCase())) {
            searches.push({
              id: doc.id,
              type: collectionName.replace('pb-', ''),
              ...data
            });
          }
        });
      }
      
      return searches;
    } catch (error) {
      console.error('Error searching content:', error);
      throw error;
    }
  },

  // Bulk operations
  async bulkUpdateStatus(bookId, collection, ids, status) {
    try {
      const batch = writeBatch(db);
      
      ids.forEach(id => {
        const docRef = doc(db, 'pb-books', bookId, collection, id);
        batch.update(docRef, { 
          status, 
          updatedAt: serverTimestamp() 
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error bulk updating status:', error);
      throw error;
    }
  }
};